import React, { useState } from 'react';
import { explainInsight, Explanation, DerivationStep } from '../services/api';
import AskField from './AskField';
import './AskExplain.css';
import { brlOrMasked } from '../utils/format';

/**
 * "De onde vem esse número?" — o campo que explica a própria tela.
 *
 * A Projeção pede mais confiança do que qualquer outra tela e explica menos:
 * dois saldos diferentes, uma coluna "Estimado" que ninguém lançou, um
 * acumulado que não bate se você somar a coluna de Gastos. Cada um tem uma
 * razão, e nenhuma delas estava visível.
 *
 * ── Por que a resposta vem em pedaços separados ──────────────────────────
 *
 * A **conta** (`steps`) e o **método** (`note`) chegam prontos do backend, e
 * nenhum dos dois passa pelo modelo: o texto é autoral e mora no código, os
 * números saem do mesmo `calculateProjections` que desenha a tabela. O modelo
 * só classificou a pergunta num assunto.
 *
 * A alternativa — deixar o modelo redigir a explicação — produz um texto
 * fluente sobre um cálculo que ele não leu. Numa tela de dinheiro isso é pior
 * que não explicar nada, porque uma explicação errada é acreditada. E quando o
 * método mudar no código, o texto daqui muda junto; um texto gerado na hora
 * nunca soube que mudou.
 */

interface Props {
  /** O olhinho de "Ocultar valores". Ver `utils/format.ts`. */
  hideValues?: boolean;
  scope: 'projection' | 'categories';
}

const EXEMPLOS: Record<Props['scope'], string[]> = {
  projection: [
    'qual a diferença entre em caixa e saldo acumulado?',
    'de onde vem esse acúmulo?',
    'o que é a coluna estimado?',
    'quando eu fico no vermelho?',
  ],
  categories: [
    'para onde meu dinheiro foi?',
    'o que subiu no período?',
    'o que entra nessa análise?',
  ],
};

const fmt = (v: number, hideValues?: boolean) => brlOrMasked(v, !!hideValues);

/** Negrito simples do texto autoral. Sem biblioteca: são só `**...**`. */
const comNegrito = (texto: string): React.ReactNode =>
  texto.split(/(\*\*[^*]+\*\*)/g).map((parte, i) =>
    parte.startsWith('**') && parte.endsWith('**')
      ? <strong key={i}>{parte.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{parte}</React.Fragment>,
  );

const SINAL: Record<DerivationStep['kind'], string> = {
  base: '', add: '+', subtract: '−', result: '=', plain: '',
};

const AskExplain: React.FC<Props> = ({ scope, hideValues }) => {
  const [aberto, setAberto] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  return (
    <div className="ax">
      <button
        className={`ax-toggle${aberto ? ' open' : ''}`}
        onClick={() => setAberto(v => !v)}
        aria-expanded={aberto}
      >
        <span className={`ax-caret${aberto ? ' open' : ''}`}>▸</span>
        De onde vem esse número?
      </button>

      {aberto && (
        <div className="ax-body">
          {/* A casca (input, seta, spinner, dicas, erro) vem de AskField. Antes
              era um vocabulário `ax-*` próprio, cópia do `tl-ask-*` de
              Transações. O que sobrou aqui é só o resultado, que é o que
              realmente difere entre as três telas. */}
          <AskField<Explanation>
            placeholder={EXEMPLOS[scope][0]}
            ariaLabel="Perguntar como este número é calculado"
            exemplos={[...EXEMPLOS[scope]]}
            onAsk={async texto => {
              setAviso(null);
              const r = await explainInsight(texto, scope);
              if (!r.explanation) throw new Error(r.problem ?? 'Não entendi a pergunta.');
              if (r.unresolved) setAviso(`Não encontrei “${r.unresolved}” nas suas categorias.`);
              return r.explanation;
            }}
          >
            {(exp, limpar) => (
              <div className="ax-card">
                <div className="ax-card-head">
                  <h4>{exp.title}</h4>
                  <button className="ax-limpar" onClick={() => { setAviso(null); limpar(); }}>limpar</button>
                </div>

                {exp.steps.length > 0 && (
                  <div className="ax-steps">
                    {exp.steps.map((s, i) => (
                      <div key={i} className={`ax-step ax-step-${s.kind}`}>
                        <span className="ax-step-sign">{SINAL[s.kind]}</span>
                        <span className="ax-step-label">
                          {s.label}
                          {s.hint && <em className="ax-step-hint">{s.hint}</em>}
                        </span>
                        <span className="ax-step-value">{s.value != null ? fmt(s.value, hideValues) : ''}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="ax-note">
                  {exp.note.split('\n\n').map((p, i) => <p key={i}>{comNegrito(p)}</p>)}
                </div>

                {(exp.caveats.length > 0 || aviso) && (
                  <div className="ax-caveats">
                    {aviso && <div className="ax-caveat">{aviso}</div>}
                    {exp.caveats.map((c, i) => <div className="ax-caveat" key={i}>{c}</div>)}
                  </div>
                )}
              </div>
            )}
          </AskField>
        </div>
      )}
    </div>
  );
};

export default AskExplain;
